/**
 * Changelog / Historial de Versiones
 *
 * Mantener actualizado con cada cambio significativo.
 * Seguir formato Semantic Versioning: MAJOR.MINOR.PATCH
 *
 * - MAJOR: Cambios incompatibles con versiones anteriores
 * - MINOR: Nueva funcionalidad compatible hacia atrás
 * - PATCH: Correcciones de bugs compatibles hacia atrás
 */

export const APP_VERSION = "1.20.0";

export const changelog = [
  {
    version: "1.20.0",
    date: "2026-03-24",
    title: "BH Dashboard + Agent System + Specialist Referrals Services + Architecture Docs",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Behavioral Health Dashboard with 7 panels",
        details: "New BHDashboard with Overview, Risk, Substance Use, ER Utilization, Unmanaged Patients, Care Programs, and Outreach panels. Location-based data filtering with mock JSON data per center."
      },
      {
        category: "feature",
        description: "Agent system for AI-assisted development and auditing",
        details: "7 specialized agents (Arqui, Bocki, Fransis, Clift, Astheron, Rafiky, Norman) with role definitions, daily tasks, and work reports. First full-system audit completed identifying 7 critical, 14 high, and 10 medium findings."
      },
      {
        category: "feature",
        description: "Specialist Referrals backend services and SP documentation",
        details: "Added specialistReferralsService.js and labPhlebotomyService.js with real API integration. Includes SP documentation for Groups 1-5 covering CRUD operations, comments, and catalog endpoints."
      },
      {
        category: "refactor",
        description: "Lab Phlebotomy migrated from pages/ to modules/ pattern",
        details: "Moved LabPhlebotomy from src/pages/Tools/Apps/LabPhlebotomy/ to src/modules/lab-phlebotomy/ following the self-contained module architecture. Old location deleted."
      },
      {
        category: "improvement",
        description: "CEO Dashboard v2 data structure and Care Manager dashboard data updates",
        details: "New DataDashCEO_v2/ with granular JSON files for cost, coding, quality, performance, population, engagement, and trends. Updated CareManager and Clinical dashboard data for all 11 locations."
      },
      {
        category: "docs",
        description: "ARCHITECTURE.md, TEAM.md, and presentation documentation",
        details: "Master architecture document consolidating system overview, stack, 3-tier architecture, 15 modules, 6 dashboards, 5 DBs, AI Gateway, RBAC, and Constitution rules. Team documentation for 7 agents. 19 presentation slides and 15 module docs."
      },
      {
        category: "refactor",
        description: "Removed legacy service files and old LabPhlebotomy location",
        details: "Deleted 13 legacy getPatientInfo.js files from src/utils/services/APP/ and old LabPhlebotomy pages. Cleaned up unused service patterns."
      }
    ]
  },
  {
    version: "1.19.0",
    date: "2026-02-19",
    title: "Employee Documents — Document Management Module",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "New Employee Documents module for managing work-related files",
        details: "Self-contained module at /employee-documents with file selection (metadata only — no file content stored), DataGrid table with search and category filters, detail dialog for editing category/description, and delete functionality. Data persists in localStorage."
      },
      {
        category: "feature",
        description: "Drag & drop upload area with multi-file support",
        details: "Visual drop zone with dashed border and CloudUpload icon. Supports click-to-browse and drag-and-drop. Extracts only file metadata (name, size, type) from File objects — never reads file content via FileReader."
      },
      {
        category: "feature",
        description: "Universal access for all authenticated roles",
        details: "Module added to UNIVERSAL_ROUTES and ROUTE_PERMISSIONS with null (no specific permission required). DOCUMENTS_READ and DOCUMENTS_WRITE permissions added to PCP and Care Management roles. Accessible from Administration sidebar section."
      },
    ],
  },
  {
    version: "1.18.0",
    date: "2026-02-17",
    title: "CEO Executive Dashboard — 7 Panels with Real Data",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Complete CEO Executive Dashboard with 7 data-driven panels",
        details: "Replaced mock-data CEO dashboard with full implementation consuming real consolidated JSON data (37 KB across 7 files). Includes: Panel 1 (Population Overview with health distribution bar), Panel 2 (Cost Analysis with Nivo donut chart + expandable PCP/facility tables), Panel 3 (Quality Measures with HEDIS progress bars + targets), Panel 4 (Center Performance heatmap table with alerts), Panel 5 (Trends line chart + Top Performers medals), Panel 6 (Coding Revenue with HCC recapture rates), Panel 7 (Engagement & Growth with visit breakdown + contact availability)."
      },
      {
        category: "feature",
        description: "Consolidated data service for executive view",
        details: "New ceoDataService loads 7 JSON datasets in parallel (no location filter — CEO sees all centers). useCEOData hook provides individual loading states per panel. Help content (InfoButton) added to every panel header."
      },
      {
        category: "improvement",
        description: "Interactive charts and sortable tables for executive analysis",
        details: "Nivo PieChart for cost breakdown, Nivo LineChart for care gap trends (last 18 months), sortable Center Performance table with color-coded heatmap, expandable tables with toggle buttons, HEDIS compliance bars with target markers."
      },
    ],
  },
  {
    version: "1.17.0",
    date: "2026-02-17",
    title: "Care Manager Dashboard — 7 Panels + Task Integration",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Complete Care Manager Dashboard with 7 specialized panels",
        details: "Replaced mock data CareManager dashboard with full implementation consuming real JSON data from 11 locations. Includes: Panel 1 (KPI Overview with 4 clickable cards), Panel 2 (Transition of Care with LACE scores), Panel 3 (Unmanaged High Risk sorted by severity), Panel 4 (ER & Utilization with PPV-ER risk), Panel 5 (Outreach with contact info links), Panel 6 (My Tasks with full CRUD), Panel 7 (Polypharmacy Alerts with lab status)."
      },
      {
        category: "feature",
        description: "Location-aware data loading for Care Management",
        details: "New careManagerDataService loads 6 JSON datasets per location in parallel. useCareManagerData hook provides individual loading states per panel. LocationSelector reused from Clinical dashboard with CM-specific data source."
      },
      {
        category: "feature",
        description: "Task system integration across all CM panels",
        details: "Every panel has action buttons that open TaskModal pre-filled with patient data: Transitions → Follow-up task, Unmanaged Risk → Care Plan task, ER → Intervention task, Outreach → Patient Recall task, Polypharmacy → Med Reconciliation task. Reuses existing useTasks hook and localStorage persistence."
      },
      {
        category: "feature",
        description: "Contextual help icons for all 7 CM panels",
        details: "InfoButton with detailed help content (cmHelpContent.js) explaining each panel's purpose, columns, color coding, and available actions."
      },
    ],
  },
  {
    version: "1.16.1",
    date: "2026-02-17",
    title: "Clinical Dashboard — CTA Connections + Info Icons + Polish",
    type: "patch",
    changes: [
      {
        category: "feature",
        description: "Contextual help info icons on all dashboard panels",
        details: "InfoButton + InfoModal components added to all 6 panels and LocationSelector. Each info icon opens a modal explaining how the panel works, what each column means, and how to interpret colors and flags."
      },
      {
        category: "feature",
        description: "CTA buttons connecting all panels to the Task system",
        details: "Panel 2 (Alerts): Follow Up button on each patient card opens TaskModal pre-filled. Panel 3 (Worklist): Task icon per patient row. Panel 4 Tab Care Gaps: + Create Task button. Panel 4 Tab No Visit: Start Recall Campaign button. All CTAs pre-fill TaskModal with relevant category and patient data."
      },
      {
        category: "fix",
        description: "Fixed PanelTasks loading state that was hardcoded to false",
        details: "PanelTasks now receives actual loading state from useClinicalDashboard hook, allowing skeleton loaders to display during data loading."
      },
      {
        category: "improvement",
        description: "Task completion animation with fade effect",
        details: "Completing a task now shows a smooth 400ms animation: checkbox marks, text gets strike-through, row fades to 40% opacity and scales down slightly before updating state."
      }
    ]
  },
  {
    version: "1.16.0",
    date: "2026-02-17",
    title: "Clinical Dashboard — Full Refactor with 6 Panels + Task System",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "New Clinical Dashboard with 11-location selector and real clinical data",
        details: "Replaces mock PCP Dashboard. LocationSelector loads 11 locations from JSON data with persistence in localStorage. DashboardSubheader shows source date, patient count, and provider count."
      },
      {
        category: "feature",
        description: "Panel 1: KPI Overview with 6 segment cards + 2 composite cards",
        details: "Clickable KPI cards (Assigned, High Cost, High Risk, Emergency, Discharged, Admitted) navigate to PatientMap. Composite cards for Care Gaps and Coding Opportunities with progress bars and sub-metrics."
      },
      {
        category: "feature",
        description: "Panel 2: Real-Time Alerts (Admitted/Discharged/Emergency)",
        details: "3-column layout with scrollable alert cards. Each card shows patient name, facility, admit date, days since admission, LACE score badge (color-coded), behavioral health flag, diagnoses, PCP. Admitted column has pulse animation."
      },
      {
        category: "feature",
        description: "Panel 3: My Patients Worklist with clinical triage sort",
        details: "Filterable patient table (search, health status, visit timeframe, provider). 10 columns with inline flags (inpatient, discharged, high risk, polypharmacy, trigger, new member). Triage sort: admitted first, then discharged, then severity, then visit recency, then gaps. Row border-left color for Complex/End of Life/At Risk. 50 rows per page."
      },
      {
        category: "feature",
        description: "Panel 4: Insights with 3 tabs (Care Gaps, Coding, No Visit)",
        details: "Care Gaps tab: SVG donut ring, compliance bars per measure (color-coded by threshold). Coding tab: condition type chips, top HCCs list. No Visit tab: distribution mini-cards, percentage stat, contactability info. Fade transitions between tabs."
      },
      {
        category: "feature",
        description: "Panel 5: Patient Recall with distribution stats and contact table",
        details: "4 mini-stat cards (12+Mo, 9Mo, 6Mo, with phone). Recall table with patient name, since chip, status, gaps, clickable phone (tel:) and email (mailto:) links, provider, and Create Task action."
      },
      {
        category: "feature",
        description: "Panel 6: Task Management System with localStorage persistence",
        details: "CRUD task system (create, complete, cancel, delete) with 9 categories, 3 priorities, 4 statuses. TaskStats counters (Due Today, Overdue, Done Today). Grouped task list (Overdue, Due Today, Upcoming, Completed). Task items with checkbox, priority dot, category chip, due date badge."
      },
      {
        category: "feature",
        description: "TaskModal for creating/editing tasks with pre-fill from all panels",
        details: "Modal with react-hook-form: title, description, category, priority, due date, patient autocomplete. Pre-fills from Recall table Create Task CTA. Patient search filters by name or CIP ID with 2+ char threshold."
      },
      {
        category: "improvement",
        description: "Responsive layout for all clinical dashboard panels",
        details: "Full 6-panel grid at 1200px+. Panels 3+4 and 5+6 in 65%/35% side-by-side layout on large screens, stacking vertically on smaller. KPIs grid: 6 cols on md+, 3 on sm, 2 on xs."
      }
    ]
  },
  {
    version: "1.15.0",
    date: "2026-02-16",
    title: "Administration Module — Sidebar layout with Master Data & Roles",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "New Administration module with sidebar navigation",
        details: "Replaces old Roles & Privileges module. Sidebar with 2 sections: Master Data (Employees, Locations, PCP Configuration) and Roles & Permissions. Consumes real backend APIs via masterDataService + userManagementService."
      },
      {
        category: "feature",
        description: "Employees section with integrated user account creation",
        details: "DataGrid with search, filters (status, location, job position), admission date column. 'User Account' column shows 'Create User' button for employees without account. Detail dialog includes: general info, user account management (deactivate, unlock, reset password, force logout), assigned locations (add/remove), and PCP config panel for PCP employees."
      },
      {
        category: "feature",
        description: "Locations section with full CRUD and status management",
        details: "DataGrid with search and status filter. Create/edit form with manager dropdown, address, coordinates, clinical flag. Shows employee count and center key per location."
      },
      {
        category: "feature",
        description: "PCP Configuration section for managing PCP assignments",
        details: "Standalone section to register, edit, deactivate, and reactivate PCP configurations. DataGrid shows capacity, patient count, usage %, age range, acceptance flags, days/week. Filter by location and status."
      },
      {
        category: "feature",
        description: "Roles & Permissions with dual view: by user and by role",
        details: "Roles by User: autocomplete user selector, view/assign/revoke roles, force logout. Permissions by Role: select role, view CRUD permissions matrix across all system modules (read-only)."
      },
      {
        category: "refactor",
        description: "Removed old Roles & Privileges module with mock data",
        details: "Deleted src/modules/roles-privileges/ (18 files) which used secureStorage mock data. 3 placeholder functions added for SPs not yet built (getJobPositions, getUsers, getRoles) returning static data until backend implements the endpoints."
      },
    ],
  },
  {
    version: "1.14.0",
    date: "2026-02-16",
    title: "MasterData Service + Real Login Flow with Role Selection",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "MasterData service with 19 API endpoint functions",
        details: "New masterDataService.js covering all MasterData controller endpoints: employee info, locations CRUD, employees CRUD, employee-location assignments, PCP config management, role-modules, and statuses catalog. Full JSDoc with request/response examples."
      },
      {
        category: "feature",
        description: "Real post-login flow replacing hardcoded data",
        details: "AuthContext.signIn now calls getEmployeeInfo, getEmployeeLocations, and getRoleModules after login. User data (fullName, roles, locations, modules) is populated from backend instead of hardcoded values."
      },
      {
        category: "feature",
        description: "Multi-role selection dialog after login",
        details: "Users with 2+ roles see a RoleSelectorDialog to pick their active role. Single-role users are auto-selected. switchRole function allows changing roles during the session."
      },
      {
        category: "refactor",
        description: "AuthContext userData structure updated with roles, locations, and modules",
        details: "New userData includes: roles array, activeRole, locations with primary flag, modules with CRUD permissions, and legacy compatibility fields (rol, location, employee)."
      },
    ],
  },
  {
    version: "1.13.0",
    date: "2026-02-13",
    title: "Care Plan Wizard Dialog, Food Traffic Light, UI Mejorada & Print",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Wizard dialog with mode selection for AI Care Plan generation",
        details: "New dialog flow lets users choose between 'Care Plan Only' or 'Care Plan & Meal Plan'. The meal plan option uses a 2-step wizard (Step 1: Goals & Habits, Step 2: Dietary & Cultural preferences). Back navigation returns to mode selection"
      },
      {
        category: "feature",
        description: "Food Traffic Light classification in meal plan",
        details: "New accordion section classifies foods into 4 color-coded levels: green (eat freely), yellow (moderate), orange (preferably avoid), red (must avoid completely). Each food chip shows a tooltip with the reason. AI prompt updated to generate foodTrafficLight data"
      },
      {
        category: "improvement",
        description: "Enhanced meal plan UI with colors and icons",
        details: "Gradient header on meal plan card, colored nutrition target cards (amber/pink/indigo/teal), meal-type colored cards (breakfast amber, lunch green, dinner blue) with FreeBreakfast/LunchDining/DinnerDining icons, styled hydration box with LocalDrink icon"
      },
      {
        category: "feature",
        description: "Print buttons for care plan and meal plan",
        details: "New print icon buttons on care plan and meal plan card headers. Opens a clean print window with patient info header, section content, and basic print CSS"
      }
    ]
  },
  {
    version: "1.12.0",
    date: "2026-02-13",
    title: "AI Meal Plan Integration in Care Plans",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "AI-Powered personalized meal plan generation",
        details: "New mealPlanAiService.js generates a 7-day culturally appropriate meal plan using OpenAI, based on care plan results, allergies, dietary preferences and cultural food traditions. Includes nutrition targets, weekly plan (3 meals/day), foods to avoid, allergy-safe alternatives, snack suggestions and hydration guidelines"
      },
      {
        category: "feature",
        description: "Dietary Preferences and Cultural Diet fields in AI dialog",
        details: "Two new input fields in the Care Plan AI dialog: Dietary Preferences/Restrictions (low-sodium, vegetarian, diabetic, etc.) and Cultural/Regional Diet (Colombian, Haitian, Mexican, etc.) to personalize the meal plan"
      },
      {
        category: "improvement",
        description: "Removed manual Care Plan creation button",
        details: "Only the AI-powered generation button remains. Button text updated to 'Generate Care Plan & Meal Plan with AI'. Meal plan generates automatically in background after care plan completes"
      }
    ]
  },
  {
    version: "1.11.0",
    date: "2026-02-06",
    title: "Care Plans - AI-Powered Care Plan Generation + AdvancedMD Integration",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "AI-Powered Care Plan generation system",
        details: "New AI service (carePlanAiService.js) generates comprehensive structured care plans using OpenAI from clinical notes, allergies, physician goals and patient habits. Returns identified problems with ICD-10, goals, interventions, allergy safety notes, preventive recommendations, lifestyle modifications, follow-up plan and risk assessment"
      },
      {
        category: "feature",
        description: "AdvancedMD patient match in Care Plans page",
        details: "When selecting a patient in /Careplan, the system automatically matches against AdvancedMD, fetches clinical notes (Annual Wellness, Follow Up Exam, Telehealth, Hospital Discharge) and allergies. Displays match status chip and Chart ID"
      },
      {
        category: "feature",
        description: "Clinical Notes and Allergies cards in Care Plans page",
        details: "New Allergies (AdvancedMD) card with table (Allergy, Reaction, Treatment, Status) and Clinical Notes (AdvancedMD) card with table (Date, Type, Template, Chief Complaint, Assessment) displayed when AdvancedMD match succeeds"
      },
      {
        category: "feature",
        description: "AI Care Plan creation dialog with full result visualization",
        details: "Create Care Plan with AI button opens dialog to input goals and patient habits. AI result displayed with accordion sections: Risk Assessment, Identified Problems, Goals, Interventions, Allergy Safety Notes, Preventive Recommendations, Lifestyle Modifications and Follow Up Plan"
      },
      {
        category: "improvement",
        description: "Removed Introduction step from Care Plan creation wizard",
        details: "The /create-care-plan/:id route now goes directly to Diagnoses/Allergies/Habits step, skipping the Introduction screen"
      }
    ]
  },
  {
    version: "1.10.0",
    date: "2026-02-06",
    title: "Clinical Info - Rutas Individuales por Sub-Modulo",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "5 rutas individuales para sub-modulos de Clinical Info",
        details: "Nuevas paginas standalone para Discharged, Admitted, Emergency, High Cost y High Risk accesibles desde el sidebar. Cada pagina wrappea solo su provider, optimizando API calls"
      },
      {
        category: "improvement",
        description: "Sidebar actualizado con 5 items individuales",
        details: "La seccion Care Management ahora muestra Discharged, Admitted, Emergency, High Cost y High Risk como items separados con iconos y colores propios, reemplazando el item unico Clinical Info"
      },
      {
        category: "improvement",
        description: "Soporte i18n para nuevos items de navegacion",
        details: "Claves nav.discharged, nav.admitted, nav.emergency, nav.highCost, nav.highRisk agregadas en los 3 idiomas (en, es, cr)"
      },
      {
        category: "docs",
        description: "Documentacion de sub-modulos de Clinical Info",
        details: "5 archivos .md en src/modules/clinical-info/docs/ describiendo que existe y que falta en cada sub-modulo (tablas, columnas, filtros, charts)"
      },
      {
        category: "refactor",
        description: "AppRouter usa routes map para clinical-info",
        details: "La ruta /ClinicalInfo y las 5 nuevas rutas se manejan con clinicalInfoRoutes.map(), siguiendo el patron de mainRoutes y carePlansRoutes"
      }
    ]
  },
  {
    version: "1.9.0",
    date: "2026-02-06",
    title: "User Profile + Patient Profiles Refactor",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Pagina de perfil del usuario logueado",
        details: "Nueva pagina /profile muestra datos del usuario (nombre, rol, ubicacion, email, employee ID) con boton para cambiar contrasena. Link 'My Profile' en UserMenu ahora muestra info del usuario en vez del wizard de pacientes"
      },
      {
        category: "refactor",
        description: "Patient Profiles movido al sidebar Care Management",
        details: "El wizard de evaluacion de pacientes ahora esta disponible en /patient-profiles con acceso directo desde el sidebar seccion Care Management. Modulo wrapper en modules/care-management-system/patient-profiles/"
      },
      {
        category: "improvement",
        description: "Soporte i18n para User Profile y Patient Profiles",
        details: "Claves userProfile.* y nav.patientProfiles agregadas en los 3 idiomas (en, es, cr)"
      }
    ]
  },
  {
    version: "1.8.0",
    date: "2026-02-06",
    title: "Layout Redesign - Modern SaaS Healthcare Shell",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Sidebar oscuro con navegacion por secciones RBAC",
        details: "Nuevo sidebar con tema oscuro (#071E24), navegacion organizada en 3 secciones (Clinical, Care Management, Administration) con filtrado por permisos. Modo colapsado/expandido persistente en localStorage"
      },
      {
        category: "feature",
        description: "View as Role para administradores",
        details: "Los administradores pueden simular la vista de cualquier rol del sistema desde el menu de usuario. Solo afecta visibilidad del sidebar, no bloquea rutas"
      },
      {
        category: "feature",
        description: "Menu de usuario enriquecido con avatar",
        details: "Avatar circular como trigger, popover con info del usuario (nombre, rol, ubicacion), acceso a perfil, selector de rol (admin), y logout con estilo"
      },
      {
        category: "improvement",
        description: "AppBar simplificado a 2 tabs",
        details: "Solo Dashboard y Patient Map en la barra superior. La navegacion de modulos se movio al sidebar con secciones colapsables"
      },
      {
        category: "fix",
        description: "Corregido bug de ancho dinamico del contenido",
        details: "El ancho del area de contenido principal ahora respeta el estado colapsado/expandido del sidebar en vez de estar hardcodeado a 240px"
      },
      {
        category: "refactor",
        description: "Configuracion de navegacion centralizada",
        details: "Nuevo archivo navigationConfig.js como fuente unica de verdad para rutas, permisos, iconos e i18n keys del sidebar y AppBar"
      },
      {
        category: "improvement",
        description: "Soporte i18n completo para navegacion",
        details: "Claves nav.* agregadas en los 3 idiomas (en, es, cr) para todos los items de navegacion"
      },
      {
        category: "improvement",
        description: "Roles del sistema expandidos a 10",
        details: "UserRoleName en Enum.js ahora incluye los 10 roles: CEO, Admin, Physician, Nurse, Care Manager, Care Coordinator, Billing, Coder, Reception, Support"
      }
    ]
  },
  {
    version: "1.7.0",
    date: "2026-02-05",
    title: "AI Specific Conditions - Refactor Self-Contained",
    type: "minor",
    changes: [
      {
        category: "improvement",
        description: "Refactor de Specific Conditions (ClinicalAiPanels) a componente self-contained",
        details: "El panel ahora obtiene datos directamente de AdvancedMD Context y Redux en vez de depender de datos pre-procesados del padre. Usa buildHedisContextText para contexto clínico completo (demographics, notas, meds, labs, allergies)"
      },
      {
        category: "improvement",
        description: "Botón Generate manual en vez de auto-trigger",
        details: "El análisis de condiciones ya no se ejecuta automáticamente al montar el componente. El usuario debe presionar 'Generate Condition Analysis with AI' para iniciar, evitando API calls innecesarias"
      },
      {
        category: "improvement",
        description: "Prompt de IA mejorado con separación system/user",
        details: "El prompt ahora usa mensajes system y user separados, temperatura reducida a 0.15, y enfatiza que solo use diagnósticos explícitamente documentados en los datos clínicos"
      },
      {
        category: "refactor",
        description: "Eliminado hook useClinicalAiSections y limpieza de props",
        details: "Se eliminó el hook intermediario y se simplificaron los props de AiReportsPanel y PatientMapPage removiendo clinicalNotesForAiPanels"
      }
    ]
  },
  {
    version: "1.6.0",
    date: "2026-02-02",
    title: "Cambio de Contraseña Obligatorio - Seguridad de Acceso",
    type: "minor",
    changes: [
      {
        category: "security",
        description: "Cambio obligatorio de contraseña por defecto",
        details: "Los usuarios que inician sesión con la contraseña por defecto (TestMCM2025) son redirigidos a una página de cambio de contraseña antes de poder acceder al sistema"
      },
      {
        category: "feature",
        description: "Nueva página de cambio de contraseña",
        details: "Página standalone /ChangePassword con la misma estética del Login, dos campos (contraseña actual y nueva) con eye toggle, validación de mínimo 8 caracteres y que sea diferente a la actual"
      },
      {
        category: "security",
        description: "Flujo seguro post-cambio de contraseña",
        details: "Después de cambiar la contraseña exitosamente, se cierra la sesión automáticamente y se redirige al Login para re-autenticarse con la nueva contraseña"
      }
    ]
  },
  {
    version: "1.5.0",
    date: "2026-01-31",
    title: "Roles & Privileges - Módulo de Administración RBAC",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Módulo completo de Roles & Privileges con 3 tabs",
        details: "Tab Users (CRUD usuarios, toggle activo/inactivo), Tab Roles (CRUD roles con checkboxes de permisos), Tab Module Access (matriz de acceso roles vs módulos)"
      },
      {
        category: "feature",
        description: "Gestión de usuarios con DataGrid",
        details: "Tabla de usuarios con búsqueda, creación, edición, y toggle de estado activo/inactivo con diálogo de confirmación"
      },
      {
        category: "feature",
        description: "Gestión de roles con permisos granulares",
        details: "36 permisos organizados por categoría (Clinical, Administrative, AI, Support) con checkboxes agrupados por módulo y Select All"
      },
      {
        category: "feature",
        description: "Matriz de acceso a módulos",
        details: "Tabla matrix de 15 módulos x 11 roles con niveles: No Access, Read Only, Read & Write, Full Access. Admin bloqueado en Full Access"
      },
      {
        category: "feature",
        description: "Capa de datos mock con secureStorage",
        details: "Datos seed (11 roles, 36 permisos, 15 módulos, 8 usuarios) en secureStorage encriptado. Servicios preparados para swap a API real"
      }
    ]
  },
  {
    version: "1.4.1",
    date: "2026-01-28",
    title: "Fix - Usuario en Sidebar con secureStorage",
    type: "patch",
    changes: [
      {
        category: "fix",
        description: "Corregido usuario undefined en Sidebar",
        details: "SidebarComponent ahora usa useAuthUser hook para leer usuario encriptado en lugar de localStorage directo"
      }
    ]
  },
  {
    version: "1.4.0",
    date: "2026-01-28",
    title: "Coding Opportunities - Integración de Comentarios Backend",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Integración de comentarios en Coding Opportunities",
        details: "Nuevo endpoint POST /api/CodingOpportunities/comment para guardar comentarios cuando el doctor modifica una decisión"
      },
      {
        category: "feature",
        description: "Integración de comentarios de paciente en Finish Process",
        details: "Nuevo endpoint POST /api/CodingOpportunities/patient-comment para guardar comentarios al finalizar el proceso"
      },
      {
        category: "improvement",
        description: "Funciones de comentarios agregadas al CodingOpContext",
        details: "Nuevas funciones addCodingOpComment y addPatientComment en el contexto para centralizar la lógica"
      },
      {
        category: "improvement",
        description: "Uso correcto de employeeId del usuario logueado",
        details: "Todos los endpoints de Coding Opportunities ahora usan user.employee para identificar al usuario"
      }
    ]
  },
  {
    version: "1.3.0",
    date: "2026-01-28",
    title: "Lab Phlebotomy - Integración Backend API",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Integración de endpoint POST para Lab Phlebotomy",
        details: "Nuevo servicio createLabPhlebotomy.js para guardar registros en base de datos en lugar de localStorage"
      },
      {
        category: "feature",
        description: "Tarjeta de información del paciente mejorada",
        details: "Nueva tarjeta visual que muestra CPID, Member ID, DOB, edad, género, teléfono, email y estado (ACTIVE/INACTIVE)"
      },
      {
        category: "improvement",
        description: "IDs numéricos para PCP Providers y Appointment Types",
        details: "Constantes actualizadas con IDs del backend para mapeo correcto con la API"
      },
      {
        category: "improvement",
        description: "Staff auto-asignado del usuario logueado",
        details: "El campo staff ahora se asigna automáticamente desde user.employee, eliminando selección manual"
      },
      {
        category: "refactor",
        description: "Simplificación del formulario de Lab Phlebotomy",
        details: "Eliminados campos redundantes (Member ID, DOB, Phone, Mobile) ya que la info se muestra en la tarjeta del paciente"
      },
      {
        category: "docs",
        description: "Documentación de migración Lab Phlebotomy",
        details: "Agregado MIGRATION_STATUS.md con estado actual y pendientes de la migración a backend"
      }
    ]
  },
  {
    version: "1.2.0",
    date: "2026-01-28",
    title: "Dashboards por Rol - Sistema Multi-Dashboard",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Dashboards específicos por rol de usuario",
        details: "MainPage ahora renderiza dashboards diferentes según el rol: CEO, Admin, PCP, Care Manager"
      },
      {
        category: "feature",
        description: "CEO Executive Dashboard",
        details: "Nuevo dashboard con KPIs poblacionales, métricas financieras, calidad HEDIS y tendencias"
      },
      {
        category: "feature",
        description: "Admin System Dashboard",
        details: "Dashboard de administración con estadísticas de usuarios, salud del sistema y logs de auditoría"
      },
      {
        category: "feature",
        description: "Care Manager Dashboard",
        details: "Dashboard de gestión de cuidados con worklist, outreach, referrals y tareas"
      },
      {
        category: "feature",
        description: "Dashboard Switcher para Admin",
        details: "Panel de preview que permite a administradores ver todos los dashboards disponibles"
      },
      {
        category: "feature",
        description: "Componentes compartidos reutilizables",
        details: "MetricCard, KPICard, ChartCard y DashboardHeader con diseño moderno y gradientes"
      },
      {
        category: "security",
        description: "Agregado rol CEO al sistema RBAC",
        details: "Nuevo rol CEO con permisos de lectura para métricas ejecutivas y reportes"
      },
      {
        category: "improvement",
        description: "Rediseño visual de dashboards",
        details: "Nuevo diseño con gradientes, sombras, iconos animados y efectos hover mejorados"
      },
      {
        category: "refactor",
        description: "PCP Dashboard extraído de MainPage",
        details: "Dashboard clínico ahora es un componente independiente con diseño mejorado"
      }
    ]
  },
  {
    version: "1.1.0",
    date: "2026-01-25",
    title: "HIPAA Compliance - Fase 2 y 3",
    type: "minor", // major, minor, patch
    changes: [
      {
        category: "security",
        description: "Implementado RBAC (Control de Acceso Basado en Roles)",
        details: "9 roles definidos con 30+ permisos granulares por módulo"
      },
      {
        category: "security",
        description: "Implementado Sistema de Auditoría",
        details: "Registro automático de acceso a datos de pacientes con 25+ tipos de eventos"
      },
      {
        category: "security",
        description: "Implementada Desensibilización de PHI para OpenAI",
        details: "Sanitización automática de datos sensibles antes de enviar a IA (Safe Harbor Method)"
      },
      {
        category: "feature",
        description: "Módulo de Soporte Técnico",
        details: "Panel de debugging con captura de logs y API calls, solo para usuarios autorizados"
      },
      {
        category: "feature",
        description: "Página de Acceso Denegado",
        details: "Nueva página /Unauthorized cuando el usuario no tiene permisos"
      }
    ]
  },
  {
    version: "1.0.0",
    date: "2026-01-24",
    title: "HIPAA Compliance - Fase 1",
    type: "major",
    changes: [
      {
        category: "security",
        description: "Removidas credenciales de API del código fuente",
        details: "Las claves de OpenAI ahora se cargan desde variables de entorno"
      },
      {
        category: "security",
        description: "Implementado Timeout de Sesión",
        details: "Auto-logout después de 15 minutos de inactividad con modal de advertencia"
      },
      {
        category: "security",
        description: "Implementado Almacenamiento Seguro",
        details: "Datos de usuario encriptados con AES-256 en sessionStorage"
      },
      {
        category: "security",
        description: "Configuración HTTPS preparada",
        details: "URLs HTTPS listas para activar cuando backend tenga SSL"
      },
      {
        category: "config",
        description: "Creado .env.example",
        details: "Template de variables de entorno para nuevos desarrolladores"
      }
    ]
  },
  {
    version: "0.9.0",
    date: "2026-01-23",
    title: "Pre-HIPAA Baseline",
    type: "minor",
    changes: [
      {
        category: "feature",
        description: "Módulo de Coding Opportunities v3",
        details: "Nueva versión del worklist con análisis de IA"
      },
      {
        category: "feature",
        description: "Módulo de Care Gaps actualizado",
        details: "Mejoras en la interfaz y filtros"
      },
      {
        category: "feature",
        description: "Specialist Referrals Module",
        details: "Nuevo módulo self-contained para referidos"
      }
    ]
  }
];

/**
 * Obtener cambios por categoría
 */
export const getChangesByCategory = (version) => {
  const release = changelog.find(r => r.version === version);
  if (!release) return {};

  return release.changes.reduce((acc, change) => {
    if (!acc[change.category]) acc[change.category] = [];
    acc[change.category].push(change);
    return acc;
  }, {});
};

/**
 * Obtener última versión
 */
export const getLatestVersion = () => changelog[0];

/**
 * Categorías de cambios
 */
export const CHANGE_CATEGORIES = {
  security: { label: "Seguridad", color: "#f44336", icon: "🔒" },
  feature: { label: "Nueva Funcionalidad", color: "#4caf50", icon: "✨" },
  fix: { label: "Corrección", color: "#ff9800", icon: "🐛" },
  improvement: { label: "Mejora", color: "#2196f3", icon: "⚡" },
  config: { label: "Configuración", color: "#9c27b0", icon: "⚙️" },
  docs: { label: "Documentación", color: "#607d8b", icon: "📚" },
  refactor: { label: "Refactorización", color: "#795548", icon: "♻️" },
  deprecated: { label: "Deprecado", color: "#9e9e9e", icon: "⚠️" }
};

export default changelog;
